/**
 * The findings register and the finding detail: punch list and response loop.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * WORK_ORDER_M3_CONFIGURATION_AND_SURFACE.md section 4.2 screen 5. The register is
 * server-paginated. The detail carries the finding's facts, the punch-list actions for
 * a comment finding, and the response loop thread: the contractor proposes, the
 * operator agrees or returns, the contractor implements, the operator verifies and
 * closes.
 *
 * THE ESCALATION SUGGESTION IS THE PRIVACY PROOF IN THIS FILE
 *   `promotionSuggested` arrives null exactly when the server masked it for a
 *   counterparty, so the whole escalation section renders only when the field is a
 *   value. There is no flag, no role check and no branch on who signed in: the
 *   suggestion UI exists on the screen only for a principal whose API answer carries
 *   it, and the same is true of the assessor's name, which is whatever the server sent.
 *
 * The register's filters are the API's own; a filter the API does not have does not
 * exist on this screen.
 */

import { useMemo, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { QueueBanner } from "../components/QueueBanner";
import { HistoryButton } from "../components/HistoryDrawer";
import { isQueueId, QUEUES } from "../nav/queues";

import { ApiError } from "../api/client";
import {
  acceptPunch,
  completePunch,
  declareImplementation,
  getFinding,
  insertInPunchList,
  listFindings,
  listPunchLists,
  listResponses,
  proposeResponse,
  decideResponse,
  distributeFinding,
  rejectPunch,
  setLessonLearned,
  suggestPromotion,
  promoteFinding,
  verifyFindingClosure,
  type FindingDetail,
  type FindingResponse,
  type FindingSummary,
  type PunchListSummary,
} from "../api/findings";
import { BarChart } from "../charts/BarChart";
import { useToken } from "../auth/sessionContext";
import { strings, term } from "../brand/strings";
import { PICKER_CLASS, PickerOption, pickerWidth } from "../nav/PickerOption";
import { ActionButton } from "@/components/ui/action-button";
import { ActionDialog } from "@/components/ui/action-dialog";
import { Button } from "@/components/ui/button";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { DialogFooter } from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { Toast } from "@/components/ui/toast";
import {
  AbsencePanel,
  Breadcrumbs,
  EmptyState,
  Facts,
  RefusalBox,
  Section,
  SkeletonFacts,
  SkeletonRows,
  StateChip,
  day,
  filterSet,
  plainLabel,
  stamp,
} from "./parts";
import {
  FINDING_DONE,
  PUNCH_DONE,
  ProjectFilterBanner,
  isPunchItem,
  useProjectFilter,
} from "./projectFilter";
import { useQuery } from "./useQuery";
import { ServerErrorScope, ServerField } from "../lib/serverErrors";
import { mayActAs, useViewerSide } from "../lib/actor";
import { listTargetAttachments } from "../api/attachments";
import { FilePicker } from "@/components/ui/file-picker";
import { ActionDialog as ScopedDialog } from "../nav/ActionDialog";
import { DownloadLink } from "./Inspections";

const PAGE_SIZE = 25;

const FINDING_CLASSES = ["NEGATIVE_OBSERVATION", "COMMENT", "POSITIVE_OBSERVATION", "OFI"] as const;

type RegisterFindingClass = (typeof FINDING_CLASSES)[number];

/**
 * The findings register's own lifecycle (the vocabulary's "finding_status" tokens).
 * The State dropdown lists THESE and not the global workflow vocabulary, most of
 * whose states no finding can ever carry.
 */
const FINDING_REGISTER_STATES = [
  "OPEN",
  "RECORDED",
  "IN_PUNCH_LIST",
  "PUNCH_COMPLETED",
  "AWAITING_RESPONSE",
  "RESPONSE_PROPOSED",
  "AGREED",
  "IMPLEMENTED",
  "PROMOTED",
  "CLOSED",
] as const;

function isFindingClass(value: string | null): value is RegisterFindingClass {
  return value !== null && (FINDING_CLASSES as readonly string[]).includes(value);
}

/** A register card: its title, then its states or its DataTable (UX_SPEC 2.1). */
function RegisterCard({
  title,
  children,
}: {
  readonly title?: string | undefined;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="q-ui mb-(--q-s2) flex min-w-0 flex-col gap-3">
      {title !== undefined && (
        <h3 className="text-xs tracking-[0.16em] text-muted-foreground uppercase">{title}</h3>
      )}
      {children}
    </section>
  );
}

/** The findings register columns: what each cell says in plain words. */
function findingRegisterColumns(): RegisterColumn<FindingSummary>[] {
  const copy = strings.findings;
  return [
    {
      id: "finding",
      label: copy.columnFinding,
      text: (row) => `${row.humanId} ${row.description}`,
      sortValue: (row) => row.humanId,
      className: "min-w-[14rem]",
      render: (row) => (
        <>
          <Link
            to={`/findings/${row.id}`}
            className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline"
          >
            {row.humanId}
          </Link>
          <div className="max-w-[34rem] [overflow-wrap:anywhere] text-word">{row.description}</div>
        </>
      ),
    },
    {
      id: "class",
      label: copy.columnClass,
      text: (row) => term(row.findingClass),
      filter: true,
    },
    {
      id: "observedBy",
      label: copy.observedByLabel,
      text: (row) => plainLabel(row.observedByName),
      sortValue: (row) => plainLabel(row.observedByName),
      filter: true,
    },
    {
      id: "observedOn",
      label: copy.observedOnLabel,
      text: (row) => day(row.observedAt),
      sortValue: (row) => row.observedAt,
      className: "whitespace-nowrap",
    },
    {
      id: "daysOpen",
      label: copy.columnDaysOpen,
      text: (row) => (row.daysOpen !== null ? String(row.daysOpen) : ""),
      sortValue: (row) => row.daysOpen ?? -1,
      className: "whitespace-nowrap",
    },
    {
      id: "state",
      label: copy.columnState,
      text: (row) => term(row.findingStatus),
      filter: true,
      render: (row) => <StateChip state={row.findingStatus} />,
    },
  ];
}

export function FindingsPage(): React.JSX.Element {
  const token = useToken();
  const [parameters, setParameters] = useSearchParams();
  const offset = Number(parameters.get("offset") ?? "0");
  const actParam = parameters.get("act");
  const classParam =
    parameters.get("class") ??
    (actParam === "record" ? "POSITIVE_OBSERVATION,NEGATIVE_OBSERVATION" : null);
  const stateParam = parameters.get("state");
  const classSet = filterSet(classParam);
  const stateSet = filterSet(stateParam);
  const stage = parameters.get("stage");
  const discipline = parameters.get("discipline");
  // Wave b12 frontend item 12: a Home card's figure opens the register narrowed to its
  // project (the server's project_id) and to what it counted (`view`: the open findings,
  // or the open punch items), by the rules the card's server count uses.
  const project = useProjectFilter();
  const view = parameters.get("view");

  const register = useQuery(
    () =>
      listFindings(token, {
        projectId: project?.id ?? null,
        findingClass:
          classSet.length === 1 && isFindingClass(classSet[0] ?? "")
            ? (classSet[0] as RegisterFindingClass)
            : null,
        findingStatus: stateSet.length > 0 ? stateSet : null,
        stageId: stage,
        disciplineId: discipline,
        limit: PAGE_SIZE,
        offset,
      }),
    [token, classParam, stateParam, stage, discipline, offset, project?.id],
  );

  const queueParam = parameters.get("queue");
  const queue = isQueueId(queueParam) ? QUEUES[queueParam] : null;

  const setParameter = (name: string, value: string): void => {
    const next = new URLSearchParams(parameters);
    if (value.length === 0) {
      next.delete(name);
    } else {
      next.set(name, value);
    }
    next.delete("offset");
    if (name === "state") {
      next.delete("queue");
    }
    setParameters(next);
  };

  const columns = useMemo(() => findingRegisterColumns(), []);

  const items = useMemo(() => {
    if (register.kind !== "ready") {
      return [];
    }
    return register.data.items.filter(
      (finding) =>
        (classSet.length === 0 || classSet.includes(finding.findingClass)) &&
        (stateSet.length === 0 || stateSet.includes(finding.findingStatus)) &&
        (view === "open"
          ? !isPunchItem(finding) && !FINDING_DONE.includes(finding.findingStatus)
          : view === "punch"
            ? isPunchItem(finding) && !PUNCH_DONE.includes(finding.findingStatus)
            : true),
    );
  }, [register, classSet, stateSet, view]);

  return (
    <>
      <header className="q-ui mb-(--q-s1) flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1 basis-[28rem]">
          <h2 className="q-shell__heading">{strings.findings.heading}</h2>
          <p className="q-shell__lede">{strings.findings.lede}</p>
        </div>
      </header>

      {queue !== null && (
        <QueueBanner
          label={queue.label}
          count={register.kind === "ready" ? register.data.total : null}
          wholeRegister="/findings"
        />
      )}
      {project !== null && (
        <ProjectFilterBanner
          label={project.label}
          view={
            view === "punch"
              ? strings.home.cardPunchOpen
              : view === "open"
                ? strings.home.cardFindingsOpen
                : strings.findings.heading
          }
          register="/findings"
        />
      )}

      {register.kind === "ready" && (
        <BarChart
          ariaLabel={`${strings.findings.heading} · ${strings.common.status}`}
          frameClassName="q-chart__frame--lead"
          tickAngle={-35}
          data={FINDING_REGISTER_STATES.map((state) => ({
            label: term(state),
            value: register.data.items.filter((row) => row.findingStatus === state).length,
          }))}
          onSelectDatum={(datum) => {
            const token = FINDING_REGISTER_STATES.find((state) => term(state) === datum.label);
            if (token !== undefined) {
              setParameter("state", token);
            }
          }}
        />
      )}

      <RegisterCard>
        {register.kind === "loading" && <SkeletonRows />}
        {register.kind === "absence" && <AbsencePanel />}
        {register.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {register.kind === "ready" &&
          (register.data.items.length === 0 ? (
            <EmptyState
              title={strings.findings.stateEmptyTitle}
              body={strings.findings.stateEmptyBody}
            />
          ) : (
            <DataTable
              data={items}
              columns={columns}
              caption={strings.findings.heading}
              testId="findings"
              getRowId={(row) => row.id}
            />
          ))}
      </RegisterCard>
    </>
  );
}

export function FindingDetailPage(): React.JSX.Element {
  const token = useToken();
  const { findingId = "" } = useParams();
  const [reload, setReload] = useState(0);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const viewerSide = useViewerSide();
  const [notice, setNotice] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: "info" | "danger" } | null>(null);

  const finding = useQuery(() => getFinding(token, findingId), [token, findingId, reload]);
  const responses = useQuery(() => listResponses(token, findingId), [token, findingId, reload]);
  const punchLists = useQuery(() => listPunchLists(token, { status: "OPEN" }), [token]);

  const reread = (): void => {
    setReload((n) => n + 1);
  };

  async function run(action: () => Promise<void>, message: string | null): Promise<void> {
    setActionError(null);
    setNotice(null);
    try {
      await action();
      if (message !== null) {
        // The action-dialog pattern's toast (M5 P5e #8): the submit's outcome
        // lands in the timed toast bar; refusals stay in place below.
        setToast({ message, tone: "info" });
      }
      reread();
    } catch (error) {
      if (error instanceof ApiError) {
        setActionError(error);
      }
    }
  }

  if (finding.kind === "absence") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.findings.heading}</h2>
        <AbsencePanel />
      </>
    );
  }
  if (finding.kind !== "ready") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.findings.heading}</h2>
        {finding.kind === "loading" && <SkeletonFacts fields={7} />}
        {finding.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
      </>
    );
  }

  const record: FindingDetail = finding.data;
  const punchListItems = punchLists.kind === "ready" ? punchLists.data.items : [];
  const punchListName =
    record.punchListName ?? punchListItems.find((pl) => pl.id === record.punchListId)?.name ?? null;
  // The escalation section renders only when the server sent a value. Null is the
  // server's mask, and null means this principal never sees this section at all.
  const suggestionVisible = record.promotionSuggested !== null;
  const lessonMarkable = mayActAs(viewerSide, "operator");

  return (
    <ServerErrorScope error={actionError}>
      <Breadcrumbs
        trail={[{ label: strings.findings.heading, to: "/findings" }, { label: record.humanId }]}
      />
      <div className="q-actions-row" style={{ alignItems: "center" }}>
        <h2 className="q-shell__heading" style={{ margin: 0 }}>
          {record.humanId}
        </h2>
        <HistoryButton objectType="FINDING" objectId={record.id} recordLabel={record.humanId} />
      </div>
      <p className="q-shell__lede">
        <span data-testid="finding-status">
          <StateChip state={record.findingStatus} />
        </span>{" "}
        · {term(record.findingClass)}
      </p>

      {toast !== null && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onDone={() => {
            setToast(null);
          }}
        />
      )}

      {notice !== null && (
        <div className="q-banner" role="status">
          <p className="q-thread__body">{notice}</p>
        </div>
      )}
      {actionError !== null && <RefusalBox error={actionError} />}

      <div className="q-section-bands" data-testid="finding-detail-sections">
        <Section heading={strings.findings.heading}>
          <Facts
            entries={[
              [strings.common.humanId, <span key="id">{record.humanId}</span>],
              [
                strings.findings.observedByLabel,
                <span key="observed" data-testid="observed-by">
                  {plainLabel(record.observedByName)}
                </span>,
              ],
              [strings.findings.observedOnLabel, <span key="at">{stamp(record.observedAt)}</span>],
              [
                strings.findings.discoveredAfterRelease,
                <span key="escape">
                  {record.discoveredAfterRelease ? strings.common.yes : strings.common.no}
                </span>,
              ],
              [
                strings.findings.subjectLabel,
                <span key="subject">{record.subjectItemRef ?? ""}</span>,
              ],
              [
                strings.findings.closureLabel,
                <span key="closure">
                  {record.closureReference ?? ""}
                  {record.closedAt !== null && ` · ${day(record.closedAt)}`}
                </span>,
              ],
              [
                strings.findings.punchRejectReason,
                <span key="reject">{record.punchRejectionReason ?? ""}</span>,
              ],
              ...(punchListName !== null
                ? [
                    [
                      strings.findings.punchListLabel,
                      <span key="punch-list">{punchListName}</span>,
                    ] as [string, ReactNode],
                  ]
                : []),
            ]}
          />
          {record.origin !== null && (
            <div className="q-banner q-gap-top" role="note" data-testid="finding-origin">
              <p className="q-banner__heading">{strings.findings.originHeading}</p>
              <p className="q-thread__body">
                {strings.findings.originItem(
                  record.origin.itemNumber,
                  record.origin.itemText,
                  record.origin.checklistCode,
                )}
              </p>
              <p className="q-thread__body">
                <Link className="q-link" to={`/inspections/${record.origin.inspectionId}`}>
                  {strings.findings.originInspection(
                    record.origin.inspectionHumanId,
                    record.origin.inspectionTitle,
                  )}
                </Link>
                {" · "}
                {record.origin.source === "PLANNED_ACTIVITY" &&
                record.origin.activityId !== null ? (
                  <Link className="q-link" to={`/activities/${record.origin.activityId}`}>
                    {strings.findings.originActivity(record.origin.activityHumanId ?? "")}
                  </Link>
                ) : (
                  <Link className="q-link" to={`/rfis/${record.origin.inspectionId}`}>
                    {record.origin.source === "OPERATOR_REQUEST"
                      ? strings.findings.originOperatorRequest
                      : strings.findings.originRfi}
                  </Link>
                )}
              </p>
            </div>
          )}
          <p className="q-thread__body q-gap-top" data-testid="finding-description">
            {record.description}
          </p>
          {record.items.length > 0 && (
            <ul>
              {record.items.map((entry) => (
                <li className="q-thread__body" key={entry.id}>
                  {entry.itemText}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {suggestionVisible && (
          <Section heading={strings.findings.promotionHeading}>
            <div data-testid="promotion-suggest">
              <p className="q-note">
                {term(record.promotionSuggested ?? "NONE") === ""
                  ? strings.findings.promotionNone
                  : term(record.promotionSuggested ?? "NONE")}
              </p>
              <PromotionControls
                record={record}
                onSuggest={(value) => {
                  void run(async () => {
                    await suggestPromotion(token, record.id, value);
                  }, null);
                }}
                onPromote={(type, title, description) => {
                  void run(async () => {
                    await promoteFinding(token, record.id, {
                      action_type: type,
                      title: title.length > 0 ? title : null,
                      description: description.length > 0 ? description : null,
                    });
                  }, null);
                }}
              />
              {record.actionRequestId !== null && (
                <Link
                  className="q-link"
                  to={`/actions/${record.actionRequestId}`}
                  data-testid="action-linked"
                >
                  {strings.actions.detailHeading}
                </Link>
              )}
            </div>
          </Section>
        )}

        {record.findingClass === "COMMENT" &&
          (record.findingStatus === "OPEN" || record.punchListId !== null) && (
            <Section heading={strings.findings.punchHeading}>
              <PunchControls
                record={record}
                punchLists={punchListItems}
                punchListName={punchListName}
                onInsert={(punchListId) => {
                  void run(async () => {
                    await insertInPunchList(token, record.id, punchListId);
                  }, null);
                }}
                onComplete={(notes) => {
                  void run(async () => {
                    await completePunch(token, record.id, notes);
                  }, null);
                }}
                onAccept={(closureReference, closureComments) => {
                  void run(async () => {
                    await acceptPunch(token, record.id, {
                      closure_reference: closureReference.length > 0 ? closureReference : null,
                      closure_comments: closureComments.length > 0 ? closureComments : null,
                    });
                  }, null);
                }}
                onReject={(reason) => {
                  void run(async () => {
                    await rejectPunch(token, record.id, reason);
                  }, null);
                }}
              />
            </Section>
          )}

        {(record.findingStatus === "CLOSED" || record.lessonLearnedStatus !== "NONE") && (
          <Section heading={strings.lessons.markHeading}>
            <div data-testid="lesson-marking">
              <p className="q-note" data-testid="lesson-current">
                {lessonLabelOf(record.lessonLearnedStatus)}
              </p>
              {/* Wave W1.5: marking a lesson is the operator's (the server refuses
                anyone else), so a contractor reads the status and is offered nothing. */}
              {record.findingStatus === "CLOSED" && lessonMarkable && (
                <LessonControls
                  onMark={(status, note) => {
                    void run(async () => {
                      await setLessonLearned(token, record.id, status, note);
                    }, strings.lessons.markSuccess);
                  }}
                />
              )}
            </div>
          </Section>
        )}

        <ImplementationSection
          record={record}
          onDone={(message) => {
            setToast({ message, tone: "info" });
            reread();
          }}
        />

        <ResponseLoopSection
          record={record}
          responses={responses.kind === "ready" ? responses.data : []}
          onPropose={(correction, correctiveAction, targetDate) => {
            void run(async () => {
              await proposeResponse(token, record.id, {
                proposed_correction: correction,
                proposed_corrective_action: correctiveAction.length > 0 ? correctiveAction : null,
                proposed_target_date: targetDate,
              });
            }, null);
          }}
          onDecide={(responseId, decision, comment) => {
            void run(async () => {
              await decideResponse(token, record.id, responseId, decision, comment);
            }, null);
          }}
          onDistribute={(comment) => {
            void run(async () => {
              await distributeFinding(token, record.id, comment);
            }, strings.findings.distributeDone);
          }}
        />

        <Section heading={strings.itps.historyHeading}>
          <div className="q-thread">
            {record.transitions.length === 0 && <p className="q-note">{strings.common.empty}</p>}
            {record.transitions.map((entry) => (
              <article className="q-thread__entry" key={entry.id}>
                <p className="q-thread__meta">
                  <strong>{term(entry.event)}</strong> · {term(entry.fromState)} →{" "}
                  {term(entry.toState)} · {stamp(entry.at)}
                  {entry.actorDisplayName !== null && ` · ${entry.actorDisplayName}`}
                </p>
                {entry.comment !== null && (
                  <p className="q-thread__body">
                    {cleanPhaseComment(entry.comment, punchListItems, punchListName)}
                  </p>
                )}
              </article>
            ))}
          </div>
        </Section>
      </div>
    </ServerErrorScope>
  );
}

/**
 * The promotion action row (M5 P6 #1): the suggestion select stays a compact
 * control; the promote form (type, title, description) opens in the ActionDialog
 * overlay instead of living embedded in the page body.
 */
function PromotionControls({
  record,
  onSuggest,
  onPromote,
}: {
  record: FindingDetail;
  onSuggest: (value: "NONE" | "NCR" | "CAR") => void;
  onPromote: (type: string, title: string, description: string) => void;
}): React.JSX.Element {
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteType, setPromoteType] = useState("NCR");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  return (
    <>
      <div className="q-actions-row" data-testid="promotion-actions">
        <Field className="flex-1">
          <Label htmlFor="promotion-select">{strings.findings.promotionSuggestLabel}</Label>
          <ServerField field="promotion_suggested">
            <NativeSelect
              id="promotion-select"
              value={record.promotionSuggested ?? "NONE"}
              onChange={(event) => {
                const value = event.target.value as "NONE" | "NCR" | "CAR";
                onSuggest(value);
              }}
            >
              <option value="NONE">{strings.findings.promotionNone}</option>
              <option value="NCR">{strings.findings.promotionNcr}</option>
              <option value="CAR">{strings.findings.promotionCar}</option>
            </NativeSelect>
          </ServerField>
        </Field>
        <Button
          variant="outline"
          data-testid="promotion-open"
          onClick={() => {
            setPromoteOpen(true);
          }}
        >
          {strings.findings.promoteButton(strings.terms.actionTypes.NCR)}
        </Button>
      </div>
      {promoteOpen && (
        <ActionDialog
          open
          title={strings.findings.promoteButton(
            strings.terms.actionTypes[promoteType as "NCR" | "CAR"],
          )}
          onClose={() => {
            setPromoteOpen(false);
          }}
        >
          <div className="flex flex-col gap-4">
            <Field>
              <Label htmlFor="promotion-type">
                {strings.findings.promoteButton(strings.actions.heading)}
              </Label>
              <ServerField field="action_type">
                <NativeSelect
                  id="promotion-type"
                  value={promoteType}
                  onChange={(event) => {
                    setPromoteType(event.target.value);
                  }}
                >
                  <option value="NCR">{strings.terms.actionTypes.NCR}</option>
                  <option value="CAR">{strings.terms.actionTypes.CAR}</option>
                </NativeSelect>
              </ServerField>
            </Field>
            <Field>
              <Label htmlFor="promotion-title">{strings.findings.promoteTitleLabel}</Label>
              <ServerField field="title">
                <Input
                  id="promotion-title"
                  value={title}
                  onChange={(event) => {
                    setTitle(event.target.value);
                  }}
                />
              </ServerField>
            </Field>
            <Field>
              <Label htmlFor="promotion-description">
                {strings.findings.promoteDescriptionLabel}
              </Label>
              <ServerField field="description">
                <Textarea
                  id="promotion-description"
                  value={description}
                  onChange={(event) => {
                    setDescription(event.target.value);
                  }}
                />
              </ServerField>
            </Field>
          </div>
          <DialogFooter className="mt-4 flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setPromoteOpen(false);
              }}
            >
              {strings.common.cancel}
            </Button>
            <Button
              data-testid="promotion-promote"
              onClick={() => {
                onPromote(promoteType, title, description);
                setPromoteOpen(false);
              }}
            >
              {promoteType === "NCR"
                ? strings.findings.promoteButton(strings.terms.actionTypes.NCR)
                : strings.findings.promoteButton(strings.terms.actionTypes.CAR)}
            </Button>
          </DialogFooter>
        </ActionDialog>
      )}
    </>
  );
}

function PunchControls({
  record,
  punchLists,
  punchListName,
  onInsert,
  onComplete,
  onAccept,
  onReject,
}: {
  record: FindingDetail;
  punchLists: PunchListSummary[];
  punchListName: string | null;
  onInsert: (punchListId: string) => void;
  onComplete: (notes: string) => void;
  onAccept: (closureReference: string, closureComments: string) => void;
  onReject: (reason: string) => void;
}): React.JSX.Element {
  const [punchListId, setPunchListId] = useState("");
  const [notes, setNotes] = useState("");
  const [closureReference, setClosureReference] = useState("");
  const [closureComments, setClosureComments] = useState("");
  const [reason, setReason] = useState("");
  // The punch forms open in the ActionDialog (M5 P6 seat W #1); the page body
  // carries only the compact opener row.
  const [dialog, setDialog] = useState<"complete" | "accept" | "reject" | null>(null);
  // Wave W1.5: accepting or rejecting completed punch work is the operator's (the
  // server refuses anyone else); the contractor is no longer offered it.
  const operatorActs = mayActAs(useViewerSide(), "operator");

  // Wave W2 (Mahmoud's ruling 2026-10-10): putting a finding on a punch list and marking
  // a punch item complete are the OPERATOR'S acts. The controls render only on the
  // server's answer for this caller; the counterparty reads who does it instead.
  const punchActs = record.mayActOnPunch;
  const showOperatorNote =
    !punchActs &&
    !operatorActs &&
    (record.findingStatus === "OPEN" || record.findingStatus === "IN_PUNCH_LIST");

  const openPunchLists = punchLists.filter((entry) => entry.status === "OPEN");

  return (
    <>
      {showOperatorNote && (
        <p className="q-note" data-testid="punch-operator-note">
          {strings.findings.punchOperatorNote}
        </p>
      )}
      {punchListName !== null && (
        <p className="q-note" data-testid="punch-list-name">
          {strings.findings.punchListLabel}: {punchListName}
        </p>
      )}
      {(record.punchListId !== null || punchListId !== "") && (
        <div className="q-actions-row">
          <HistoryButton
            objectType="PUNCH_LIST"
            objectId={record.punchListId ?? punchListId}
            recordLabel={punchListName ?? undefined}
          />
        </div>
      )}
      {record.findingStatus === "OPEN" && punchActs && (
        <div className="q-actions-row">
          <Field className="flex-1">
            <Label htmlFor="punch-list">{strings.findings.punchListLabel}</Label>
            <ServerField field="punch_list_id">
              <NativeSelect
                id="punch-list"
                className={PICKER_CLASS}
                value={punchListId}
                onChange={(event) => {
                  setPunchListId(event.target.value);
                }}
              >
                <option value="">{strings.findings.punchListPlaceholder}</option>
                {openPunchLists.map((entry) => (
                  <PickerOption
                    key={entry.id}
                    value={entry.id}
                    identifier={entry.humanId}
                    name={entry.name}
                    width={pickerWidth(openPunchLists.map((one) => one.humanId))}
                  />
                ))}
              </NativeSelect>
            </ServerField>
          </Field>
          <Button
            variant="outline"
            disabled={punchListId.length === 0}
            onClick={() => {
              onInsert(punchListId);
            }}
          >
            {strings.findings.addToPunchList}
          </Button>
        </div>
      )}

      {record.findingStatus === "IN_PUNCH_LIST" && punchActs && (
        <div className="q-actions-row" data-testid="punch-actions">
          <Button
            variant="outline"
            data-testid="punch-complete-open"
            onClick={() => {
              setDialog("complete");
            }}
          >
            {strings.findings.markCompleted}
          </Button>
        </div>
      )}

      {record.findingStatus === "PUNCH_COMPLETED" && operatorActs && (
        <div className="q-actions-row" data-testid="punch-actions">
          {/* Invariant 2's mirror: the completer's own accept button is dead, with the
              reason beside it, instead of dying on the server's INV-2 refusal. */}
          <ActionButton
            variant="outline"
            data-testid="punch-accept-open"
            disabledReason={record.punchCompletedByMe ? strings.findings.acceptPunchBlocked : null}
            reasonTestId="punch-accept-blocked"
            onClick={() => {
              if (!record.punchCompletedByMe) {
                setDialog("accept");
              }
            }}
          >
            {strings.findings.acceptPunch}
          </ActionButton>
          <Button
            variant="outline"
            data-testid="punch-reject-open"
            onClick={() => {
              setDialog("reject");
            }}
          >
            {strings.findings.rejectPunch}
          </Button>
        </div>
      )}

      {dialog !== null && (
        <ActionDialog
          open
          title={
            dialog === "complete"
              ? strings.findings.markCompleted
              : dialog === "accept"
                ? strings.findings.acceptPunch
                : strings.findings.rejectPunch
          }
          onClose={() => {
            setDialog(null);
          }}
        >
          <div className="flex flex-col gap-4">
            {dialog === "complete" && (
              <Field>
                <Label htmlFor="punch-notes">{strings.findings.completionNotesLabel}</Label>
                <ServerField field="completion_notes">
                  <Textarea
                    id="punch-notes"
                    value={notes}
                    onChange={(event) => {
                      setNotes(event.target.value);
                    }}
                  />
                </ServerField>
              </Field>
            )}
            {dialog === "accept" && (
              <>
                <Field>
                  <Label htmlFor="punch-closure-ref">{strings.findings.punchAcceptClosure}</Label>
                  <ServerField field="closure_reference">
                    <Input
                      id="punch-closure-ref"
                      value={closureReference}
                      onChange={(event) => {
                        setClosureReference(event.target.value);
                      }}
                    />
                  </ServerField>
                </Field>
                <Field>
                  <Label htmlFor="punch-closure-comments">
                    {strings.findings.punchAcceptComments}
                  </Label>
                  <ServerField field="closure_comments">
                    <Textarea
                      id="punch-closure-comments"
                      value={closureComments}
                      onChange={(event) => {
                        setClosureComments(event.target.value);
                      }}
                    />
                  </ServerField>
                </Field>
              </>
            )}
            {dialog === "reject" && (
              <Field>
                <Label htmlFor="punch-reject-reason">{strings.findings.punchRejectReason}</Label>
                <ServerField field="comment">
                  <Textarea
                    id="punch-reject-reason"
                    value={reason}
                    onChange={(event) => {
                      setReason(event.target.value);
                    }}
                  />
                </ServerField>
              </Field>
            )}
          </div>
          <DialogFooter className="mt-4 flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setDialog(null);
              }}
            >
              {strings.common.cancel}
            </Button>
            <Button
              data-testid={`punch-${dialog}`}
              disabled={dialog === "reject" && reason.length === 0}
              title={
                dialog === "reject" && reason.length === 0
                  ? strings.actions.justificationDisabledReason
                  : undefined
              }
              onClick={() => {
                if (dialog === "complete") {
                  onComplete(notes);
                } else if (dialog === "accept") {
                  onAccept(closureReference, closureComments);
                } else {
                  onReject(reason);
                }
                setDialog(null);
              }}
            >
              {dialog === "complete"
                ? strings.findings.markCompleted
                : dialog === "accept"
                  ? strings.findings.acceptPunch
                  : strings.findings.rejectPunch}
            </Button>
          </DialogFooter>
        </ActionDialog>
      )}
    </>
  );
}

function ResponseLoopSection({
  record,
  responses,
  onPropose,
  onDecide,
  onDistribute,
}: {
  record: FindingDetail;
  responses: FindingResponse[];
  onPropose: (correction: string, correctiveAction: string, targetDate: string) => void;
  onDecide: (responseId: string, decision: "AGREE" | "REJECT", comment: string) => void;
  onDistribute?: (comment?: string | null) => void;
}): React.JSX.Element {
  const [correction, setCorrection] = useState("");
  const [correctiveAction, setCorrectiveAction] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [decisionComment, setDecisionComment] = useState("");
  const [distributeComment, setDistributeComment] = useState("");
  // The response-loop forms open in the ActionDialog (M5 P6 seat W #1): the
  // propose dialog from the opener row, the decide dialog per response round.
  const [dialog, setDialog] = useState<
    "propose" | "distribute" | { decide: "AGREE" | "REJECT"; responseId: string } | null
  >(null);

  // Wave W1.5: the contractor proposes and the operator decides. A contractor that
  // proposed used to be offered Agree and Reject and was refused only at submit.
  const side = useViewerSide();
  const proposalOpen = record.findingStatus === "AWAITING_RESPONSE" && mayActAs(side, "contractor");
  const decisionOpen = record.findingStatus === "RESPONSE_PROPOSED" && mayActAs(side, "operator");

  return (
    <Section heading={strings.findings.responseHeading}>
      {responses.length === 0 && <p className="q-note">{strings.findings.responseEmpty}</p>}
      <div className="q-thread" data-testid="response-thread">
        {responses.map((entry: FindingResponse) => (
          <article className="q-thread__entry" key={entry.id} data-testid="response-round">
            <p className="q-thread__meta">
              <strong>{strings.findings.roundLabel(entry.roundNo)}</strong> ·{" "}
              {stamp(entry.submittedAt)} · {term(entry.decision)}
            </p>
            <p className="q-thread__body">
              {strings.findings.proposedCorrection}: {entry.proposedCorrection}
            </p>
            {entry.proposedCorrectiveAction !== null && (
              <p className="q-thread__body">
                {strings.findings.proposedCorrectiveAction}: {entry.proposedCorrectiveAction}
              </p>
            )}
            <p className="q-thread__body">
              {strings.findings.proposedTargetDate}: {day(entry.proposedTargetDate)}
            </p>
            {entry.decisionComment !== null && (
              <p className="q-thread__body">
                {strings.common.comments}: {entry.decisionComment}
              </p>
            )}
            {decisionOpen && (
              <div className="q-actions-row q-gap-top">
                <Button
                  variant="outline"
                  data-testid="response-decide-agree-open"
                  onClick={() => {
                    setDialog({ decide: "AGREE", responseId: entry.id });
                  }}
                >
                  {strings.findings.agreeResponse}
                </Button>
                <Button
                  variant="outline"
                  data-testid="response-decide-reject-open"
                  onClick={() => {
                    setDialog({ decide: "REJECT", responseId: entry.id });
                  }}
                >
                  {strings.findings.rejectResponse}
                </Button>
              </div>
            )}
          </article>
        ))}
      </div>

      {record.findingStatus === "OPEN" && record.mayDistribute && (
        <div className="q-actions-row q-gap-top" data-testid="distribute-actions">
          <Button
            variant="outline"
            data-testid="finding-distribute"
            onClick={() => {
              setDialog("distribute");
            }}
          >
            {strings.findings.distributeForResponse}
          </Button>
        </div>
      )}

      {proposalOpen && (
        <div className="q-actions-row q-gap-top" data-testid="response-actions">
          <Button
            variant="outline"
            data-testid="response-propose-open"
            onClick={() => {
              setDialog("propose");
            }}
          >
            {strings.findings.proposeResponse}
          </Button>
        </div>
      )}

      {dialog !== null && (
        <ActionDialog
          open
          title={
            dialog === "distribute"
              ? strings.findings.distributeHeading
              : dialog === "propose"
                ? strings.findings.proposeResponse
                : dialog.decide === "AGREE"
                  ? strings.findings.agreeResponse
                  : strings.findings.rejectResponse
          }
          onClose={() => {
            setDialog(null);
          }}
        >
          <div className="flex flex-col gap-4">
            {dialog === "distribute" ? (
              <>
                <p className="q-shell__lede">{strings.findings.distributeLede}</p>
                <Field>
                  <Label htmlFor="distribute-comment">
                    {strings.findings.distributeCommentLabel}
                  </Label>
                  <ServerField field="comment">
                    <Textarea
                      id="distribute-comment"
                      data-testid="distribute-comment"
                      value={distributeComment}
                      onChange={(event) => {
                        setDistributeComment(event.target.value);
                      }}
                    />
                  </ServerField>
                </Field>
              </>
            ) : dialog === "propose" ? (
              <>
                <Field>
                  <Label htmlFor="response-correction">{strings.findings.proposedCorrection}</Label>
                  <ServerField field="proposed_correction">
                    <Textarea
                      id="response-correction"
                      value={correction}
                      onChange={(event) => {
                        setCorrection(event.target.value);
                      }}
                    />
                  </ServerField>
                </Field>
                <Field>
                  <Label htmlFor="response-corrective">
                    {strings.findings.proposedCorrectiveAction}
                  </Label>
                  <ServerField field="proposed_corrective_action">
                    <Textarea
                      id="response-corrective"
                      value={correctiveAction}
                      onChange={(event) => {
                        setCorrectiveAction(event.target.value);
                      }}
                    />
                  </ServerField>
                </Field>
                <Field>
                  <Label htmlFor="response-target">{strings.findings.proposedTargetDate}</Label>
                  <ServerField field="proposed_target_date">
                    <Input
                      id="response-target"
                      type="date"
                      value={targetDate}
                      onChange={(event) => {
                        setTargetDate(event.target.value);
                      }}
                    />
                  </ServerField>
                </Field>
              </>
            ) : (
              <Field>
                <Label htmlFor="decision-comment">{strings.findings.decisionCommentLabel}</Label>
                <ServerField field="comment">
                  <Textarea
                    id="decision-comment"
                    value={decisionComment}
                    onChange={(event) => {
                      setDecisionComment(event.target.value);
                    }}
                  />
                </ServerField>
              </Field>
            )}
          </div>
          <DialogFooter className="mt-4 flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setDialog(null);
              }}
            >
              {strings.common.cancel}
            </Button>
            <Button
              data-testid={
                dialog === "distribute"
                  ? "finding-distribute-confirm"
                  : dialog === "propose"
                    ? "response-propose"
                    : "response-decide"
              }
              disabled={
                dialog !== "distribute" &&
                dialog !== "propose" &&
                dialog.decide === "REJECT" &&
                decisionComment.length === 0
              }
              title={
                dialog !== "distribute" &&
                dialog !== "propose" &&
                dialog.decide === "REJECT" &&
                decisionComment.length === 0
                  ? strings.actions.justificationDisabledReason
                  : undefined
              }
              onClick={() => {
                if (dialog === "distribute") {
                  onDistribute?.(
                    distributeComment.trim().length > 0 ? distributeComment.trim() : null,
                  );
                } else if (dialog === "propose") {
                  onPropose(correction, correctiveAction, targetDate);
                } else {
                  onDecide(dialog.responseId, dialog.decide, decisionComment);
                }
                setDialog(null);
              }}
            >
              {dialog === "distribute"
                ? strings.findings.distributeSubmit
                : dialog === "propose"
                  ? strings.findings.proposeResponse
                  : dialog.decide === "AGREE"
                    ? strings.findings.agreeResponse
                    : strings.findings.rejectResponse}
            </Button>
          </DialogFooter>
        </ActionDialog>
      )}
    </Section>
  );
}

/* ---------------------------------------------------------------------- */
/* Wave R0 Break 7: the implementation leg. The contractor declares the    */
/* agreed correction implemented with its evidence; an operator who is not */
/* the declarer verifies it closed or sends it back with a reason. Both    */
/* dialogs keep their refusal inside, and the act a viewer cannot complete */
/* is not offered.                                                         */
/* ---------------------------------------------------------------------- */

function ImplementationSection({
  record,
  onDone,
}: {
  record: FindingDetail;
  onDone: (message: string) => void;
}): React.JSX.Element | null {
  const token = useToken();
  const side = useViewerSide();
  const [dialog, setDialog] = useState<"declare" | "verify" | null>(null);
  const [note, setNote] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [decision, setDecision] = useState<"CLOSE" | "REJECT">("CLOSE");
  const [reason, setReason] = useState("");
  const [closureReference, setClosureReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const declared = record.implementedAt !== null;
  const evidence = useQuery(
    () =>
      declared
        ? listTargetAttachments(token, { entityType: "FINDING", entityId: record.id })
        : Promise.resolve([]),
    [token, record.id, declared, record.implementedAt],
  );

  const status = record.findingStatus;
  if (status !== "AGREED" && status !== "IMPLEMENTED" && !declared) {
    return null;
  }
  const mayDeclare = status === "AGREED" && mayActAs(side, "contractor");
  // The declarer never verifies his own work (workflow.no_self_approval); the server's
  // answer for this caller hides the act rather than letting it be refused.
  const mayVerify =
    status === "IMPLEMENTED" && mayActAs(side, "operator") && !record.implementedByMe;
  const reasonMissing = decision === "REJECT" && reason.trim().length === 0;

  function open(which: "declare" | "verify"): void {
    setError(null);
    setNote("");
    setFiles([]);
    setDecision("CLOSE");
    setReason("");
    setClosureReference("");
    setDialog(which);
  }

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (dialog === "declare") {
        await declareImplementation(token, record.id, { note: note.trim(), files });
        setDialog(null);
        onDone(strings.findings.implementationDeclared);
      } else if (dialog === "verify") {
        await verifyFindingClosure(token, record.id, {
          decision,
          reason: reason.trim().length > 0 ? reason.trim() : null,
          closureReference: closureReference.trim().length > 0 ? closureReference.trim() : null,
        });
        setDialog(null);
        onDone(
          decision === "CLOSE"
            ? strings.findings.implementationClosed
            : strings.findings.implementationRejected,
        );
      }
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section heading={strings.findings.implementationHeading}>
      <div data-testid="finding-implementation">
        {declared && (
          <div className="q-thread" data-testid="implementation-declared">
            <article className="q-thread__entry">
              <p className="q-thread__meta">
                {strings.findings.implementationDeclaredOn(day(record.implementedAt ?? ""))}
              </p>
              {record.implementationNote !== null && (
                <p className="q-thread__body">{record.implementationNote}</p>
              )}
              {evidence.kind === "loading" && (
                <p className="q-status" role="status">
                  {strings.findings.implementationEvidenceLoading}
                </p>
              )}
              {(evidence.kind === "failed" || evidence.kind === "absence") && (
                <p className="q-note" role="alert">
                  {strings.findings.implementationEvidenceFailed}
                </p>
              )}
              {evidence.kind === "ready" && evidence.data.length === 0 && (
                <p className="q-note">{strings.findings.implementationEvidenceNone}</p>
              )}
              {evidence.kind === "ready" && evidence.data.length > 0 && (
                <ul className="q-note" data-testid="implementation-evidence">
                  {evidence.data.map((file) => (
                    <li key={file.linkId}>
                      <DownloadLink attachmentId={file.attachmentId} filename={file.filename} />
                    </li>
                  ))}
                </ul>
              )}
            </article>
          </div>
        )}

        {status === "AGREED" && !mayDeclare && (
          <p className="q-note" data-testid="implementation-waiting-contractor">
            {strings.findings.implementationAwaitingContractor}
          </p>
        )}
        {status === "IMPLEMENTED" && record.implementedByMe && (
          <p className="q-note" data-testid="implementation-own">
            {strings.findings.implementationOwn}
          </p>
        )}
        {status === "IMPLEMENTED" && !record.implementedByMe && !mayVerify && (
          <p className="q-note" data-testid="implementation-waiting-operator">
            {strings.findings.implementationAwaitingOperator}
          </p>
        )}

        {(mayDeclare || mayVerify) && (
          <div className="q-actions-row q-gap-top" data-testid="implementation-actions">
            {mayDeclare && (
              <Button
                data-testid="implementation-declare-open"
                onClick={() => {
                  open("declare");
                }}
              >
                {strings.findings.declareImplementation}
              </Button>
            )}
            {mayVerify && (
              <Button
                data-testid="implementation-verify-open"
                onClick={() => {
                  open("verify");
                }}
              >
                {strings.findings.verifyImplementation}
              </Button>
            )}
          </div>
        )}
      </div>

      {dialog !== null && (
        <ScopedDialog
          open
          title={
            dialog === "declare"
              ? strings.findings.declareImplementation
              : strings.findings.verifyImplementation
          }
          error={error ?? undefined}
          onClose={() => {
            setDialog(null);
          }}
        >
          {dialog === "declare" ? (
            <div className="flex flex-col gap-4">
              <p className="q-shell__lede">{strings.findings.declareImplementationLede}</p>
              <Field>
                <Label htmlFor="implementation-note">{strings.findings.implementationNote}</Label>
                <ServerField field="note">
                  <Textarea
                    id="implementation-note"
                    data-testid="implementation-note"
                    value={note}
                    onChange={(event) => {
                      setNote(event.target.value);
                    }}
                  />
                </ServerField>
              </Field>
              <Field>
                <Label htmlFor="implementation-files">{strings.findings.implementationFiles}</Label>
                <ServerField field="attachment_ids">
                  <FilePicker
                    files={files}
                    onChange={setFiles}
                    label={strings.findings.implementationFiles}
                    testId="implementation-files"
                    emptyText={strings.findings.implementationFilesEmpty}
                  />
                </ServerField>
              </Field>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="q-shell__lede">{strings.findings.verifyImplementationLede}</p>
              <Field>
                <Label htmlFor="implementation-decision">
                  {strings.findings.verifyDecisionLabel}
                </Label>
                <ServerField field="decision">
                  <NativeSelect
                    id="implementation-decision"
                    data-testid="implementation-decision"
                    value={decision}
                    onChange={(event) => {
                      setDecision(event.target.value === "REJECT" ? "REJECT" : "CLOSE");
                    }}
                  >
                    <option value="CLOSE">{strings.findings.verifyDecisionClose}</option>
                    <option value="REJECT">{strings.findings.verifyDecisionReject}</option>
                  </NativeSelect>
                </ServerField>
              </Field>
              <Field>
                <Label htmlFor="implementation-reason">
                  {decision === "REJECT"
                    ? strings.findings.verifyReasonRequired
                    : strings.findings.verifyReasonOptional}
                </Label>
                <ServerField field="reason">
                  <Textarea
                    id="implementation-reason"
                    data-testid="implementation-reason"
                    value={reason}
                    onChange={(event) => {
                      setReason(event.target.value);
                    }}
                  />
                </ServerField>
              </Field>
              {decision === "CLOSE" && (
                <Field>
                  <Label htmlFor="implementation-closure-ref">
                    {strings.findings.verifyClosureRef}
                  </Label>
                  <ServerField field="closure_reference">
                    <Input
                      id="implementation-closure-ref"
                      value={closureReference}
                      onChange={(event) => {
                        setClosureReference(event.target.value);
                      }}
                    />
                  </ServerField>
                </Field>
              )}
            </div>
          )}
          <DialogFooter className="mt-4 flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setDialog(null);
              }}
            >
              {strings.common.cancel}
            </Button>
            <Button
              data-testid="implementation-confirm"
              disabled={busy || (dialog === "declare" ? note.trim().length === 0 : reasonMissing)}
              title={
                dialog === "declare" && note.trim().length === 0
                  ? strings.findings.implementationNoteRequired
                  : dialog === "verify" && reasonMissing
                    ? strings.findings.verifyReasonMissing
                    : undefined
              }
              onClick={() => {
                void send();
              }}
            >
              {dialog === "declare"
                ? strings.findings.declareImplementation
                : decision === "CLOSE"
                  ? strings.findings.verifyDecisionClose
                  : strings.findings.verifyDecisionReject}
            </Button>
          </DialogFooter>
        </ScopedDialog>
      )}
    </Section>
  );
}

/* ---------------------------------------------------------------------- */
/* The lessons-learned marking flow (Ruling 18). Rendered on a CLOSED      */
/* finding; the server refuses everyone else and the refusal shows as it   */
/* arrived, exactly like every other loop's refusal.                       */
/* ---------------------------------------------------------------------- */

/** The register's displayed label for a lesson status token. */
function lessonLabelOf(status: string): string {
  if (status === "CANDIDATE") {
    return strings.lessons.statusCANDIDATE;
  }
  if (status === "CAPTURED") {
    return strings.lessons.statusCAPTURED;
  }
  return strings.lessons.statusNone;
}

function LessonControls({
  onMark,
}: {
  onMark: (status: "CANDIDATE" | "CAPTURED", note: string | null) => void;
}): React.JSX.Element {
  const [status, setStatus] = useState<"CANDIDATE" | "CAPTURED">("CANDIDATE");
  const [note, setNote] = useState("");
  return (
    <div className="flex flex-col gap-4">
      <Field>
        <Label htmlFor="lesson-status">{strings.lessons.markLabel}</Label>
        <ServerField field="status">
          <NativeSelect
            id="lesson-status"
            data-testid="lesson-status-select"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value === "CAPTURED" ? "CAPTURED" : "CANDIDATE");
            }}
          >
            <option value="CANDIDATE">{strings.lessons.markCandidate}</option>
            <option value="CAPTURED">{strings.lessons.markCaptured}</option>
          </NativeSelect>
        </ServerField>
      </Field>
      <Field>
        <Label htmlFor="lesson-note">{strings.lessons.noteLabel}</Label>
        <ServerField field="note">
          <Textarea
            id="lesson-note"
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </ServerField>
      </Field>
      <div>
        <Button
          variant="outline"
          data-testid="lesson-mark-button"
          onClick={() => {
            onMark(status, note.length > 0 ? note : null);
          }}
        >
          {strings.lessons.markButton}
        </Button>
      </div>
    </div>
  );
}

function cleanPhaseComment(
  comment: string | null,
  punchLists: readonly PunchListSummary[],
  punchListName: string | null,
): string | null {
  if (comment === null) {
    return null;
  }
  let cleaned = comment;
  for (const pl of punchLists) {
    if (cleaned.includes(pl.id)) {
      cleaned = cleaned.replaceAll(pl.id, pl.name);
    }
  }
  if (punchListName !== null) {
    cleaned = cleaned.replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      punchListName,
    );
  }
  cleaned = cleaned.replace(/\[[A-Za-z0-9_.-]+\]\s*/g, "");
  return cleaned;
}
